import React, { FC, ReactNode, ChangeEvent, FocusEvent } from 'react';
import { Field, useField, FieldInputProps, FormikContextType } from 'formik';
import { Input } from 'antd';
import Error from '../Error';
import styles from './InputField.module.scss';
import clsx from 'clsx';
import { InfoCircleOutlined } from '@ant-design/icons';

interface FormValues {
  [key: string]: string | number | null;
}

export interface InputFieldProps {
  name: string;
  placeholderstring: string;
  disabled?: boolean;
  onFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  label?: string | React.ReactNode;
  italicText?: string;
  handlechange?: (value: string | number | null) => void;
  textarea?: boolean;
  addonbefore?: ReactNode;
  addonafter?: ReactNode;
  password?: boolean;
  maxLength?: number;
  rows?: number;
  info?: boolean;
  infoClick?: () => void;
  className?: string;
  suffix?: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (event:React.KeyboardEvent<HTMLInputElement>) => void;
  value?: string | number;
  defaultValue?: string;
  isNullable?: boolean;
  isShowLabel?: boolean;
  readOnly?: boolean;
}

const InputField: FC<InputFieldProps> = InputFieldProps => {
  // eslint-disable-next-line
  const [field, meta] = useField(InputFieldProps);
  const handleShowLabel = () => {
    setshowLabel(true);
  };
  const {
    name,
    disabled,
    label,
    placeholderstring,
    textarea,
    addonbefore,
    password,
    maxLength,
    rows,
    addonafter,
    info,
    onFocus,
    infoClick,
    className,
    onBlur,
    suffix,
    italicText,
    onClick,
    isNullable = false,
    isShowLabel,
    ...remainingProps
  } = InputFieldProps;
  const [showLabel, setshowLabel] = React.useState<boolean>(
    isShowLabel ?? false
  );
  const { TextArea, Password } = Input;
  const InputFieldContainerClass = clsx({
    [styles.inputFieldContainer]: true,
    [styles.error]: meta.touched && meta.error,
  });
  const labelClass = clsx({
    [styles.labelFloat]: true,
    [styles.error]: meta.touched && meta.error,
  });

  return (
    <div className={InputFieldContainerClass}>
      {(showLabel || field.value || field.value === 0) && (
        <label htmlFor={name} className={labelClass}>
          {' '}
          {label}
          {italicText && (
            <span style={{ fontStyle: 'italic' }}>{italicText}</span>
          )}
        </label>
      )}
      <Field name={name}>
        {({ field, form: { handleBlur } }: {
          field: FieldInputProps<string | number>;
          form: FormikContextType<FormValues>;
        }) => (
          <>
            {textarea ? (
              <TextArea
                {...(field as FieldInputProps<string>)}
                rows={rows}
                id={name}
                placeholder={placeholderstring}
                onFocus={(e: FocusEvent<HTMLTextAreaElement>) => {
                  handleShowLabel();
                  onFocus && onFocus(e);
                }}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  const value = e.target.value;
                  const newValue = value === '' && isNullable ? null : value;
                  field.onChange({
                    target: { name: field.name, value: newValue },
                  });
                }}
                disabled={disabled}
                className={className}
                maxLength={maxLength}
              />
            ) : password ? (
              <Password
                {...field}
                id={name}
                addonBefore={addonbefore}
                placeholder={placeholderstring}
                onFocus={handleShowLabel}
                disabled={disabled}
                className={className}
                {...remainingProps}
              />
            ) : (
              <>
                <Input
                  {...field}
                  {...InputFieldProps}
                  id={name}
                  onBlur={(event: FocusEvent<HTMLInputElement>) => {
                    onBlur && onBlur(event);
                    handleBlur(event);
                  }}
                  maxLength={maxLength}
                  addonBefore={addonbefore}
                  addonAfter={addonafter}
                  placeholder={placeholderstring}
                  className={className}
                  onFocus={(e: FocusEvent<HTMLInputElement>) => {
                    handleShowLabel();
                    onFocus && onFocus(e);
                  }}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    const newValue = value === '' && isNullable ? null : value;
                    field.onChange({
                      target: { name: field.name, value: newValue },
                    });
                  }}
                  onClick={onClick as (event: React.MouseEvent<HTMLInputElement>) => void}
                  suffix={suffix}
                  disabled={disabled}
                  {...remainingProps}
                />
              </>
            )}
            {info && (
              <div
                className={styles.infoContainer}
                onClick={() => {
                  infoClick && infoClick();
                }}>
                <p>
                  <InfoCircleOutlined style={{ fontSize: '18px' }} />
                </p>
              </div>
            )}
            {meta.touched && meta.error && <Error message={meta.error} />}
          </>
        )}
      </Field>
    </div>
  );
};

export default InputField;
